import React, { useRef, useState, useEffect } from "react";
import { Form as AntForm, Button, Input as AntInput } from "antd";
import { Modal } from "react-bootstrap";
import { QRCode } from "react-qrcode-logo";
import { SiteLogo } from "../../containers/practices/components";
import CustomGradientColorPicker from "../../components/customGradientColorPicker";
import Editpen from "../../assets/img/edit-iconv2.svg";
import Download from "../../assets/img/download-conv2.svg";
import pencilIcon from "../../assets/img/icon-pencil.svg";
import { useMutation } from "react-apollo";
import gql from "graphql-tag";

const FormItem = AntForm.Item;

const uploadFileMutation = gql`
  mutation uploadFile($file: Upload!, $folder: String!) {
    uploadFile(file: $file, folder: $folder) {
      ok
      response {
        s3Token
        url
      }
    }
  }
`;

const qrCodeCompanyMutation = gql`
  mutation qrCodeCompany($portalId: Int!, $userId: Int!, $qrcode: JSON!) {
    qrCodeCompany(portalId: $portalId, userId: $userId, qrcode: $qrcode) {
      ok
      message
    }
  }
`;

const qrCodePracticeMutation = gql`
  mutation qrCodePractice($practiceId: Int, $userId: Int, $qrcode: JSON) {
    qrCodePractice(practiceId: $practiceId, userId: $userId, qrcode: $qrcode) {
      ok
      message
    }
  }
`;

const qrCodeUserMutation = gql`
  mutation qrCodeUser($userId: Int, $qrcode: JSON) {
    qrCodeUser(userId: $userId, qrcode: $qrcode) {
      ok
      message
    }
  }
`;

const QRCodeEditor = AntForm.create()((props) => {
  const {
    form,
    isOpen,
    onClose,
    currentPortalId,
    gblUserId,
    initialState,
    onSave,
    me,
    qrScope = "company",
    practiceId,
    practiceOwnerId,
    userId,
    account,
  } = props;
  const { getFieldDecorator } = form;

  const [isEditMode, setIsEditMode] = useState(false);
  const [isQrColor, setIsQrColor] = useState(false);
  const [isBorderColor, setIsBorderColor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrLogo, setQrLogo] = useState(null);
  const [borderColor, setBorderColor] = useState(initialState && initialState.eyecolor_0_outer ? initialState.eyecolor_0_outer : "#000000");
  const [qrColor, setQrColor] = useState(initialState && initialState.fgColor ? initialState.fgColor : "#000000");
  const [state, setState] = useState({
    removeQrCodeBehindLogo: false,
    eyecolor_0_outer: initialState && initialState.eyecolor_0_outer ? initialState.eyecolor_0_outer : "#000000",
    fgColor: initialState && initialState.fgColor ? initialState.fgColor : "#000000",
    logoPadding: "0",
    enableCORS: false,
    ecLevel: "L",
    size: "320",
    logoWidth: 76,
    logoHeight: 76,
    siteImage: initialState && initialState.siteImage ? initialState.siteImage : '',
    value: initialState && initialState.value ? initialState.value : "",
    logoImage: initialState && initialState.logoImage ? initialState.logoImage : null,
  });

  const ref = useRef(null);
  const qrColorRef = useRef(null);
  const borderColorRef = useRef(null);

  const [qrCodeUploadFile] = useMutation(uploadFileMutation);
  const [qrCodeCompanySave] = useMutation(qrCodeCompanyMutation);
  const [qrCodePracticeSave] = useMutation(qrCodePracticeMutation);
  const [qrCodeUserSave] = useMutation(qrCodeUserMutation);

  const canEditQr = () => {
    if (!me) return false;
    if (qrScope === "company") {
      return me.accountType === 1 && me.isPortalAdmin;
    }
    if (qrScope === "practice") {
      if (!account || practiceId == null) return false;
      const hasPerm =
        account.permissions &&
        (account.permissions.includes("editportal") ||
          account.permissions.includes("createpractice") ||
          account.permissions.includes("editpractice"));
      if (!hasPerm) return false;
      if (account.rolecode === "CMPYO") {
        return me.practiceid != null && practiceId != me.practiceid;
      }
      return account.accountType === 1 || account.rolecode === "CMPYM" || account.rolecode === "PRCTO";
    }
    if (qrScope === "user") {
      return account && (account.accountType === 1 || account.rolecode === "CMPYM");
    }
    return false;
  };

  useEffect(() => {
    if (isEditMode == false) return;
    if (Object.keys(state).length > 4) {
      form.setFields({
        eyecolor_0_outer: {
          value: state.eyecolor_0_outer,
        },
      });
      setBorderColor(state.eyecolor_0_outer);
      form.setFields({
        fgColor: {
          value: state.fgColor,
        },
      });
      setQrColor(state.fgColor);
    }
  }, [isEditMode, state]);

  useEffect(() => {
    if (initialState) {
    
      // Update form fields
      const fgColorValue = initialState.fgColor ? initialState.fgColor.replace("#", "") : "000000";
      const borderColorValue = initialState.eyecolor_0_outer ? initialState.eyecolor_0_outer.replace("#", "") : "000000";
      
     
      // Update state with new values
      setState(prevState => ({
        ...prevState,
        eyecolor_0_outer: "#" + borderColorValue,
        fgColor: "#" + fgColorValue,
        siteImage: initialState.siteImage ? initialState.siteImage : '',
        value: initialState.value ? initialState.value : prevState.value,
        logoImage: initialState.logoImage ? initialState.logoImage : prevState.logoImage
      }));

      // Update color states
      setBorderColor("#" + borderColorValue);
      setQrColor("#" + fgColorValue);
      
    }
  }, [initialState]);

  useEffect(() => {
    if (qrColor) {
      document.addEventListener("mousedown", handleClickOutsideQrColor);
    } else {
      document.removeEventListener("mousedown", handleClickOutsideQrColor);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideQrColor);
    };
  }, [qrColor]);

  useEffect(() => {
    if (borderColor) {
      document.addEventListener("mousedown", handleClickOutsideBorderColor);
    } else {
      document.removeEventListener("mousedown", handleClickOutsideBorderColor);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideBorderColor);
    };
  }, [borderColor]);

  const handleClickOutsideQrColor = (event) => {
    if (qrColorRef.current && !qrColorRef.current.contains(event.target)) {
      setIsQrColor(false);
    }
  };

  const handleClickOutsideBorderColor = (event) => {
    if (borderColorRef.current && !borderColorRef.current.contains(event.target)) {
      setIsBorderColor(false);
    }
  };

  const handleDownload = () => {
    ref.current.download("png", "Unitifi-qrcode");
  };

  const handleUploadParams = (uploadParams) => {
    const { file, folder } = uploadParams;
    const target = {};
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = (e) => {
        target.name = "logoImage";
        target.value = reader.result;
        target.logoName = file.name;
        target.file = file;
        target.folder = folder;
        setQrLogo(target);
      };
    } else setQrLogo(undefined);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    form.validateFields(async (err, values) => {
      if (!err) {
        if (values.eyecolor_0_outer.length < 7) values.eyecolor_0_outer = "#" + values.eyecolor_0_outer;
        if (values.fgColor.length < 7) values.fgColor = "#" + values.fgColor;
        let obj = {
          ...state,
          ...values,
          value: state.value, // Preserve the QR code value
        };
        setLoading(true);
        let isFileUpload = false;
        if (qrLogo && qrLogo.file) {
          const fileRes = await qrCodeUploadFile({ variables: { file: qrLogo.file, folder: qrLogo.folder } });
          if (fileRes.data.uploadFile && fileRes.data.uploadFile.response && fileRes.data.uploadFile.response.url) {
            obj.siteImage = fileRes.data.uploadFile.response.url;
            isFileUpload = true;
          }
        } else {
          if (typeof qrLogo == "undefined") {
            obj.siteImage = null;
            obj.logoImage = null;
          }
        }
        let logoimg = obj.logoImage;
        delete obj.logoImage;
        const currentPath = window.location.pathname;
        console.log("qrScope", qrScope);
        console.log("currentPath", currentPath);
        if (qrScope === "company") {
          await qrCodeCompanySave({
            variables: { portalId: parseInt(currentPortalId, 10), userId: gblUserId, qrcode: obj },
          });
        } else if (qrScope === "practice") {
          await qrCodePracticeSave({
            variables: {
              practiceId: parseInt(practiceId, 10),
              userId: parseInt(practiceOwnerId, 10),
              qrcode: obj,
            },
          });
        } else if (qrScope === "user") {
          await qrCodeUserSave({
            variables: { userId: parseInt(userId != null ? userId : me.id, 10), qrcode: obj },
          });
        }
        if (isFileUpload) {
          obj.logoImage = qrLogo.value;
        } else obj.logoImage = logoimg;
        setState(obj);
        setIsEditMode(false);
        if (onSave) onSave(obj);
        const shouldReloadCompany = qrScope === "company" && currentPath.includes("/company");
        const shouldReloadPractice = qrScope === "practice" && currentPath.includes("/practices/view/");
        const shouldReloadAdvisor = qrScope === "user" && (currentPath.includes("/advisors/view/") || currentPath.includes("/users/view/"));
        if (shouldReloadCompany || shouldReloadPractice || shouldReloadAdvisor) {
          window.location.reload();
        }
        setLoading(false);
      }
    });
  };

  const validateSixDigitHexColor = (_, value, callback) => {
    if (value) {
      const sixDigitHexColorRegex = /^([0-9A-Fa-f]{6})$/;
      if (value && !sixDigitHexColorRegex.test(value.replace("#", ""))) {
        callback("Please enter a valid six-digit hex color.");
      } else callback();
    } else callback();
  };

  const boroderColorHandler = (color) => {
    if (color && color.target) {
      const newColor = color.target.value.toUpperCase();
      setBorderColor("#" + newColor);
      form.setFields({
        eyecolor_0_outer: {
          value: newColor,
          errors: null
        },
      });
    } else {
      const newColor = color.hex.toUpperCase();
      setBorderColor(newColor);
      form.setFields({
        eyecolor_0_outer: {
          value: newColor.replace("#", ""),
          errors: null
        },
      });
    }
  };

  const fgColorHandler = (color) => {
    if (color && color.target) {
      setQrColor("#" + color.target.value.toUpperCase());
      form.setFields({
        fgColor: {
          value: color.target.value.toUpperCase(),
        },
      });
    } else {
      setQrColor(color.hex.toUpperCase());
      form.setFields({
        fgColor: {
          value: color.hex.toUpperCase(),
        },
      });
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={() => {
        if (isEditMode) {
          setIsEditMode(false);
        } else {
          onClose();
        }
      }}
      centered
      className="unitifi-modal-wrapper unitifi-qrcode-default-modal-wrapper">
      <Modal.Header closeButton>
          <Modal.Title id="example-modal-sizes-title-sm">
            <h4>{isEditMode && canEditQr() ? "Edit QR Code" : "View QR Code"}</h4>
          </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isEditMode ? (
          <AntForm layout="vertical" onSubmit={handleSubmit}>
            <FormItem label="QR code color">
              <div className="input-color-box-wrapper">
                <div className="input-color-code-display-wrapper">
                  {getFieldDecorator("fgColor", {
                    rules: [
                      {
                        message: "QR code color is required",
                        required: true,
                      },
                      { validator: validateSixDigitHexColor },
                    ],
                  })(
                    <div>
                      <AntInput type={"text"} placeholder="006FBA" value={qrColor && qrColor.replace("#", "")} onChange={fgColorHandler}
                      />
                      <p>#</p>
                    </div>
                  )}
                </div>
                <div className="color-picker-input" ref={qrColorRef}>
                  <div className="colorinput" onClick={() => setIsQrColor(!isQrColor)}>
                    <div className="colorbox" style={{ background: qrColor }}></div>
                    <div style={{ display: "flex", marginLeft: "5px", height: "16px", width: "16px" }}>
                      <img src={pencilIcon} alt="pencilIcon"/>
                    </div>
                  </div>
                  {isQrColor && <CustomGradientColorPicker hex={qrColor} onChange={fgColorHandler} />}
                </div>
              </div>
            </FormItem>
            <FormItem label="Border color">
              <div className="input-color-box-wrapper">
                <div className="input-color-code-display-wrapper">
                  {getFieldDecorator("eyecolor_0_outer", {
                    rules: [
                      {
                        message: "Border color is required",
                        required: true,
                      },
                      { validator: validateSixDigitHexColor },
                    ],
                  })(
                    <div>
                      <AntInput 
                        type="text" placeholder="006FBA" name="eyecolor_0_outer" value={borderColor && borderColor.replace("#", "")} onChange={boroderColorHandler}
                      />
                      <p>#</p>
                    </div>
                  )}
                </div>
                <div className="color-picker-input" ref={borderColorRef}>
                  <div className="colorinput" onClick={() => setIsBorderColor(!isBorderColor)}>
                    <div className="colorbox" style={{ background: borderColor }}></div>
                    <div style={{ display: "flex", marginLeft: "5px", height: "16px", width: "16px" }}>
                      <img src={pencilIcon} alt="pencilIcon"/>
                    </div>
                  </div>
                  {isBorderColor && <CustomGradientColorPicker hex={borderColor} onChange={boroderColorHandler} />}
                </div>
              </div>
            </FormItem>
            <div className="upload-logo-wrapper">
              <SiteLogo handleUploadParams={handleUploadParams} practice={state && state.siteImage ? { siteLogo: state.siteImage } : null} caption="Optimal logo size is 216px by 64px." />
            </div>
            <div className="modal-footer-bnts-wrapper">
              <Button type="" size="large" htmlType="button" className="unitifi-login-btn" onClick={() => setIsEditMode(false)}>
                Cancel
              </Button>
              <Button type="primary" size="large" htmlType="submit" className="unitifi-login-btn" loading={loading} disabled={loading}>
                Save
              </Button>
            </div>
          </AntForm>
        ) : (
          <>
            <div className="qr-codebox">
              <QRCode
                ref={ref}
                {...{
                  ...state,
                  eyeColor: [
                    {
                      outer: state && state.eyecolor_0_outer ? state.eyecolor_0_outer : state && state.fgColor ? state.fgColor : "#000000",
                      inner: state && state.eyecolor_0_inner !== undefined && state.eyecolor_0_inner !== null ? state.eyecolor_0_inner : state && state.fgColor !== undefined && state.fgColor !== null ? state.fgColor : "#000000",
                    },
                    {
                      outer: state && state.eyecolor_0_outer ? state.eyecolor_0_outer : state && state.fgColor ? state.fgColor : "#000000",
                      inner: state && state.eyecolor_0_inner !== undefined && state.eyecolor_0_inner !== null ? state.eyecolor_0_inner : state && state.fgColor !== undefined && state.fgColor !== null ? state.fgColor : "#000000",
                    },
                    {
                      outer: state && state.eyecolor_0_outer ? state.eyecolor_0_outer : state && state.fgColor ? state.fgColor : "#000000",
                      inner: state && state.eyecolor_0_inner !== undefined && state.eyecolor_0_inner !== null ? state.eyecolor_0_inner : state && state.fgColor !== undefined && state.fgColor !== null ? state.fgColor : "#000000",
                    },
                  ],
                }}
              />
            </div>
            <div className="edit-download-footer-div">
              {canEditQr() && (
                <Button onClick={() => setIsEditMode(true)}>
                  <i>
                    <img src={Editpen} alt="Edit" />
                  </i>
                  Edit QR Code
                </Button>
              )}
              <Button className="download-btn" onClick={handleDownload}>
                <i>
                  <img src={Download} alt="Download" />
                </i>
                Download QR Code
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
});

export default QRCodeEditor;